import { useGetPsiContractingProfile } from '@/lib/api/psi-profile';
import React, { useState, useRef, useEffect } from 'react';
import { Stack, Button, TextField, Typography } from '@mui/material';
import TextFieldWithCheck from '../../../components/form/TextFieldWithCheck.tsx';
import EmailFieldWithCheck from '../../../components/form/EmailFieldWithCheck.tsx';
import DocumentField from '../../../components/form/DocumentField.tsx';
import MuiPhone from '@/components/form/MUIPhone.tsx';
import AddressFields from '@/components/ui/AddressFields.tsx';
import ProfilePhotoDropzone from '@/features/user/update-profile/ProfilePhotoDropzone.tsx';
import SignatureSection from './SignatureSection';
import ModalAlert from '../../../components/ui/ModalAlert.tsx';
import SignaturePad from 'react-signature-canvas';
import { validateDNI, validateNIE } from '@/utils/misc.ts';
import { UpdateMeRequest, UpdateMeResponse, UserDTO } from '@inversor-hub/types';
import { AxiosError } from 'axios';
import useSnackbar from '@/hooks/useSnackbar.tsx';

interface FormData {
  publicDescription: string;
  name: string;
  surnames: string;
  documentNumber: string;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
  province: string;
  notes: string;
  documentType: string;
  photoUrl: string;
  signature?: string;
}

interface UserDetailsSectionProps {
  currentUser: UpdateMeResponse;
  signatureDataUrl: string | undefined;
  uploadProfilePhoto: (file: File) => Promise<string>;
  uploadSignature: (blob: Blob) => Promise<unknown>;
  updateMe: (data: UpdateMeRequest) => Promise<UserDTO>;
  onSuccess?: () => void;
}

const mandatoryFields: { name: keyof FormData; label: string }[] = [
  { name: 'name', label: 'Nombre' },
  { name: 'surnames', label: 'Apellidos' },
  { name: 'documentNumber', label: 'Documento' },
  { name: 'phone', label: 'Teléfono' },
  { name: 'address', label: 'Dirección' },
  { name: 'city', label: 'Localidad' },
  { name: 'postalCode', label: 'Código Postal' },
  { name: 'province', label: 'Provincia' },
];

function toFormData(user: UpdateMeResponse): FormData {
  return {
    ...user,
    surnames: user.surnames ?? '',
    phone: user.phone ?? '',
    address: user.address ?? '',
    city: user.city ?? '',
    postalCode: user.postalCode ?? '',
    province: user.province ?? '',
    signature: user.signature ?? '',
    notes: '',
    documentType: user.documentType ?? '',
    publicDescription: user.publicDescription ?? '',
    documentNumber: user.documentNumber ?? '',
    photoUrl: user.photoUrl ?? '',
  };
}

function validateDocumentNumber(documentType: string, documentNumber: string): boolean {
  if (documentType === 'DNI') {
    return validateDNI(documentNumber);
  } else if (documentType === 'NIE') {
    return validateNIE(documentNumber);
  } else if (documentType === 'Pasaporte') {
    return documentNumber.trim() !== '';
  }
  return false;
}

const LEGAL_NAME_HINT = 'Nombre y apellidos deben coincidir con tu documento de identidad.';
const SINGLE_SURNAME_HINT = `${LEGAL_NAME_HINT} Si tienes DNI español, indica los dos apellidos.`;

function getSurnamesHelperText(surnames: string): string {
  const words = surnames.trim().split(/\s+/).filter(Boolean);
  return words.length === 1 ? SINGLE_SURNAME_HINT : LEGAL_NAME_HINT;
}

interface ErrorFields {
  address?: boolean;
  documentNumber?: boolean;
  name?: boolean;
  surnames?: boolean;
  phone?: boolean;
}

const UserDetailsSectionForm: React.FC<UserDetailsSectionProps> = ({
  currentUser,
  signatureDataUrl,
  uploadProfilePhoto,
  uploadSignature,
  updateMe,
  onSuccess,
}) => {
  const contracting = useGetPsiContractingProfile(currentUser.role === 'psi');
  const actsAsCompany = currentUser.role === 'psi' && contracting.data?.identity.type === 'COMPANY';
  const [formData, setFormData] = useState<FormData>(toFormData(currentUser));
  const [fieldErrors, setFieldErrors] = useState<ErrorFields>({});
  const [documentNumberErrorMessage, setDocumentNumberErrorMessage] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [messageModal, setMessageModal] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [hasSignatureChanged, setHasSignatureChanged] = useState(false);
  const signaturePadRef = useRef<SignaturePad>(null);
  const { show: showSnackbar } = useSnackbar();
  const surnamesHelperText = getSurnamesHelperText(formData.surnames);

  useEffect(() => {
    setPhotoUrl(currentUser.photoUrl ?? '');
  }, [currentUser]);

  useEffect(() => {
    if (!signatureDataUrl) {
      signaturePadRef.current?.clear();
      return;
    }

    const img = new window.Image();
    img.onload = () => {
      const canvas = signaturePadRef.current!.getCanvas();
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      }
    };
    img.src = signatureDataUrl;
  }, [signatureDataUrl]);

  const handleDrop = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    try {
      const url = await uploadProfilePhoto(file);
      setPhotoUrl(url);
      setFormData((prev) => ({ ...prev, photoUrl: url }));
    } catch {
      showSnackbar({ title: 'No se pudo subir la foto de perfil', variant: 'error' });
    }
  };

  const handleChange = (name: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: String(value).trim() === '' }));
  };

  const handleDNIChange = (newValue: string) => {
    setFormData({ ...formData, documentNumber: newValue });
    const { documentType } = formData;
    let isValid = false;
    if (documentType === 'DNI') {
      isValid = validateDNI(newValue);
    } else if (documentType === 'NIE') {
      isValid = validateNIE(newValue);
    } else if (documentType === 'Pasaporte') {
      isValid = newValue.trim() !== '';
    }
    if (!isValid) {
      setDocumentNumberErrorMessage('El documento no es válido');
      setFieldErrors((prevErrors) => ({ ...prevErrors, documentNumber: false }));
    } else {
      setDocumentNumberErrorMessage('');
      setFieldErrors((prevErrors) => ({ ...prevErrors, documentNumber: false }));
    }
  };

  const handleDNIBlur = () => {
    const { documentType, documentNumber } = formData;
    let isValid = false;
    if (documentType === 'DNI') {
      isValid = validateDNI(documentNumber);
    } else if (documentType === 'NIE') {
      isValid = validateNIE(documentNumber);
    } else if (documentType === 'Pasaporte') {
      isValid = documentNumber.trim() !== '';
    }
    if (!isValid) {
      setDocumentNumberErrorMessage('El documento no es válido');
    } else {
      setDocumentNumberErrorMessage('');
    }
  };

  const handleDocumentTypeChange = (newDocumentType: string) => {
    setFormData({ ...formData, documentType: newDocumentType });
    setDocumentNumberErrorMessage('');
  };

  const handleAddressBlur = () => {
    setFieldErrors((prev) => ({
      ...prev,
      address: !actsAsCompany && formData.address.trim() === '',
    }));
  };

  const handleAddressChange = ({
    address,
    city,
    postalCode,
    province,
  }: {
    address: string;
    city: string;
    postalCode: string;
    province: string;
  }) => {
    setFormData((prev) => ({ ...prev, address, city, postalCode, province }));
  };

  const validatePhone = () => {
    if (!formData.phone) {
      setFieldErrors((prevErrors) => ({ ...prevErrors, phone: true }));
      setMessageModal('Por favor, ingrese un número de teléfono válido.');
      return false;
    }
    const phonePattern = /^\+34\d{9}$/;
    if (!phonePattern.test(formData.phone)) {
      setFieldErrors((prevErrors) => ({ ...prevErrors, phone: true }));
      setMessageModal(
        'El número de teléfono no es válido. Debe tener el formato +34 seguido de 9 dígitos.'
      );
      return false;
    }
    setFieldErrors((prevErrors) => ({ ...prevErrors, phone: false }));
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let formValidated = true;
    let messageModal = '';
    const missingFields = mandatoryFields.filter(
      (campo) =>
        !(actsAsCompany && ['address', 'city', 'postalCode', 'province'].includes(campo.name)) &&
        !formData[campo.name]
    );
    if (missingFields.length > 0) {
      formValidated = false;
      const missingFieldNames = missingFields.map((campo) => campo.label).join(', ');
      messageModal = `Por favor, complete los siguientes campos obligatorios: ${missingFieldNames}.`;
      const newErrors: { [key: string]: boolean } = {};
      missingFields.forEach((campo) => {
        newErrors[campo.name] = true;
      });
      setFieldErrors(newErrors);
    }
    if (!validatePhone()) {
      formValidated = false;
      setFieldErrors((prevErrors) => ({ ...prevErrors, phone: true }));
      messageModal = messageModal || 'Por favor, ingrese un Teléfono válido.';
    }
    const hasValidDocumentNumber = validateDocumentNumber(
      formData.documentType,
      formData.documentNumber
    );
    if (!hasValidDocumentNumber) {
      formValidated = false;
      messageModal = messageModal || 'Por favor, ingrese un documento válido.';
      setFieldErrors((prevErrors) => ({ ...prevErrors, documentNumber: true }));
    }
    if (!formValidated) {
      messageModal =
        messageModal || 'El formulario contiene errores. Por favor, revise los campos marcados.';
      setMessageModal(messageModal);
      setOpenModal(true);
      return;
    }
    try {
      const rest = { ...formData };
      delete rest.signature;
      if (hasSignatureChanged) {
        if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
          const signatureBlob = await getSignatureBlob();
          await uploadSignature(signatureBlob);
          await updateMe(rest);
        } else {
          await updateMe({ ...rest, signature: '' });
        }
      } else {
        await updateMe(rest);
      }
      showSnackbar({ title: 'Perfil actualizado correctamente', variant: 'success' });
      setHasSignatureChanged(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: unknown) {
      if (error instanceof AxiosError && error?.response?.status === 409) {
        setMessageModal('Este DNI ya está registrado por otro usuario.');
        setOpenModal(true);
      } else {
        showSnackbar({
          title: 'Error al guardar los datos. Inténtalo de nuevo más tarde',
          variant: 'error',
        });
      }
    }
  };

  const getSignatureBlob = (): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      if (!signaturePadRef.current) {
        reject(new Error('SignaturePad no disponible'));
        return;
      }
      signaturePadRef.current.getCanvas().toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('No se pudo generar el Blob de la firma'));
        }
      }, 'image/png');
    });
  };

  return (
    <form name="user_details" onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <TextFieldWithCheck
          id="name"
          name="name"
          label="Nombre(*)"
          variant="outlined"
          value={formData.name}
          onChange={({ target }) => handleChange(target.name, target.value)}
          onBlur={handleAddressBlur}
          error={fieldErrors.name}
        />
        <TextFieldWithCheck
          id="surnames"
          name="surnames"
          label="Apellidos(*)"
          variant="outlined"
          value={formData.surnames}
          onChange={({ target }) => handleChange(target.name, target.value)}
          error={fieldErrors.surnames}
          helperText={surnamesHelperText}
        />
        <EmailFieldWithCheck
          id="email"
          name="email"
          label="Email(*)"
          value={currentUser.email}
          disabled={true}
        />
        <DocumentField
          documentType={formData.documentType as 'DNI' | 'NIE' | 'Pasaporte'}
          value={formData.documentNumber}
          onValueChange={handleDNIChange}
          onTypeChange={(type) => handleDocumentTypeChange(type)}
          helperTextValue={documentNumberErrorMessage}
          onBlurValue={handleDNIBlur}
          errorValue={fieldErrors.documentNumber}
          FormHelperTextProps={{ style: { fontSize: '0.75rem', color: '#d32f2f' } }}
        />
        <MuiPhone
          defaultCountry="es"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={(phone: string) => {
            setFormData({ ...formData, phone });
          }}
          onBlur={validatePhone}
          error={fieldErrors.phone}
          helperText={fieldErrors.phone ? 'El teléfono no es válido' : ''}
          FormHelperTextProps={{ style: { fontSize: '0.75rem', color: '#d32f2f' } }}
        />
        {actsAsCompany ? (
          <Typography variant="body2" color="text.secondary">
            El domicilio de tus contratos se obtiene de los datos de tu empresa.
          </Typography>
        ) : (
          <AddressFields
            formData={formData}
            onChange={handleAddressChange}
            errorFields={fieldErrors}
            setErrorFields={setFieldErrors}
          />
        )}
        <TextField
          label="Descripción Pública"
          multiline
          rows={4}
          value={formData.publicDescription}
          onChange={(e) => setFormData({ ...formData, publicDescription: e.target.value })}
          variant="outlined"
          onBlur={handleAddressBlur}
          fullWidth
          margin="normal"
        />
        <ProfilePhotoDropzone onDrop={handleDrop} photoUrl={photoUrl} />
        <SignatureSection
          signatureDataUrl={signatureDataUrl}
          signaturePadRef={signaturePadRef}
          onSignatureChange={setHasSignatureChanged}
        />
        <Button variant="contained" size="large" type="submit">
          Actualizar datos personales
        </Button>
        <ModalAlert open={openModal} message={messageModal} onClose={() => setOpenModal(false)} />
      </Stack>
    </form>
  );
};

export default UserDetailsSectionForm;
